import type { ImageSource } from '../types/common';
import { IMAGE_BASE_URL } from '../config/env';

/** Relative paths returned by FileUpload / Feeds APIs for WLB-hosted media. */
const SERVER_MEDIA_FOLDER_RE =
  /^(?:UserImages|GroupImages|ConversationImages|Conversations|WLBImages|PostImages|Uploads)\//i;

/**
 * Generates the full image URL by concatenating the base URL and the provided path.
 * Returns undefined if the path is invalid/unavailable or IMAGE_BASE_URL is missing.
 *
 * @param path The relative image path (e.g., from an API response)
 * @returns The absolute URL string for the image, or undefined
 */
export const getFullImageUrl = (path?: string | null): string | undefined => {
  if (!path || typeof path !== 'string' || path.trim() === '') {
    return undefined;
  }
  // Normalize backslashes to forward slashes
  const normalizedPath = path.replace(/\\/g, '/');

  // Already absolute — return as-is regardless of domain
  if (
    normalizedPath.startsWith('http://') ||
    normalizedPath.startsWith('https://') ||
    normalizedPath.startsWith('file://') ||
    normalizedPath.startsWith('content://') ||
    normalizedPath.startsWith('data:')
  ) {
    return normalizedPath;
  }

  const imagePath = normalizedPath.replace(/^\/+/, '');

  const envBaseUrl = IMAGE_BASE_URL;
  if (!envBaseUrl) {
    return undefined;
  }
  const baseUrl = envBaseUrl.replace(/\/+$/, '');

  // WLB-hosted uploads (including GIFs under UserImages/, etc.)
  if (SERVER_MEDIA_FOLDER_RE.test(imagePath)) {
    return `${baseUrl}/${imagePath}`;
  }

  const finalPath = imagePath.startsWith('UserImages/')
    ? imagePath
    : `UserImages/${imagePath}`;

  return `${baseUrl}/${finalPath}`;
};

/**
 * Converts a full CDN URL back to the relative path the Feeds APIs expect in MediaUrl.
 * Leaves already-relative paths unchanged.
 */
export const toRelativeMediaPath = (path?: string | null): string => {
  if (!path || typeof path !== 'string' || path.trim() === '') {
    return '';
  }

  const normalized = path.trim().replace(/\\/g, '/');
  const envBaseUrl = IMAGE_BASE_URL.replace(/\/+$/, '');

  let relative = normalized;
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    if (envBaseUrl && normalized.toLowerCase().startsWith(envBaseUrl.toLowerCase())) {
      relative = normalized.slice(envBaseUrl.length).replace(/^\/+/, '');
    } else {
      try {
        relative = decodeURIComponent(new URL(normalized).pathname).replace(/^\/+/, '');
      } catch {
        return '';
      }
    }
  } else {
    relative = normalized.replace(/^\/+/, '');
    // Dev serves media through a same-origin proxy, so the base itself is relative.
    const relativeBase = envBaseUrl.startsWith('/')
      ? envBaseUrl.replace(/^\/+/, '')
      : '';
    if (
      relativeBase &&
      relative.toLowerCase().startsWith(`${relativeBase.toLowerCase()}/`)
    ) {
      relative = relative.slice(relativeBase.length + 1);
    }
  }

  return relative;
};

/** Known API placeholder profile paths that 404 — treat as no profile photo. */
export const isPlaceholderProfilePictureUrl = (url?: string | null): boolean => {
  if (!url || typeof url !== 'string') return true;
  const normalized = url.trim().toLowerCase();
  if (!normalized) return true;
  if (normalized.includes('defaultphoto.gif')) return true;
  if (normalized.includes('/profilepictures/default')) return true;
  return false;
};

/**
 * Resolves a profile-picture path/URL for display.
 * Returns undefined for empty values and known placeholder images.
 */
export const resolveProfilePictureUrl = (path?: string | null): string | undefined => {
  if (isPlaceholderProfilePictureUrl(path)) return undefined;
  const full = getFullImageUrl(path);
  if (!full || isPlaceholderProfilePictureUrl(full)) return undefined;
  return full;
};

/**
 * Like {@link resolveImageSource} but skips unusable profile-picture placeholders.
 */
export const resolveProfilePictureSource = (
  source: unknown,
): ImageSource | undefined => {
  const resolved = resolveImageSource(source);
  if (resolved == null) return undefined;
  if (typeof resolved === 'number') return resolved;
  const uriSource = Array.isArray(resolved) ? resolved[0] : resolved;
  const uri = uriSource?.uri;
  if (!uri || isPlaceholderProfilePictureUrl(uri)) return undefined;
  return uriSource;
};

/**
 * Safely resolves any image source input (string, object, require number, or nested { uri: ... })
 * into a valid image source or undefined, preventing RCTImageView native crashes.
 */
export const resolveImageSource = (
  source: unknown,
): ImageSource | undefined => {
  if (source == null) return undefined;

  // 1. Bundled assets (require('./image.png') → number)
  if (typeof source === 'number') {
    return source;
  }

  // 2. String path or URL
  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return undefined;
    const isAbsolute = /^(https?|file|content|data):/i.test(trimmed);
    if (isAbsolute) {
      return { uri: trimmed };
    }
    const fullUrl = getFullImageUrl(trimmed);
    return fullUrl ? { uri: fullUrl } : undefined;
  }

  // 3. Objects ({ uri: string } or nested { uri: { uri: string } })
  if (typeof source === 'object') {
    if ('url' in source && typeof (source as { url: unknown }).url === 'string') {
      return resolveImageSource((source as { url: string }).url);
    }
    if ('uri' in source) {
      let currentUri: unknown = (source as { uri: unknown }).uri;
      while (
        typeof currentUri === 'object' &&
        currentUri !== null &&
        'uri' in currentUri
      ) {
        currentUri = (currentUri as { uri: unknown }).uri;
      }
      if (typeof currentUri === 'string') {
        const trimmed = currentUri.trim();
        if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
          return undefined;
        }
        let finalUri = trimmed;
        const isAbsolute = /^(https?|file|content|data):/i.test(trimmed);
        if (!isAbsolute) {
          finalUri = getFullImageUrl(trimmed) || trimmed;
        }
        // Only return a clean { uri } — spreading unknown keys can crash RCTImageView.
        return { uri: finalUri };
      }
      return undefined;
    }
    // Objects without a usable `uri` (e.g. `{ url: '...' }`) are not valid RN Image sources.
    return undefined;
  }

  return undefined;
};
